/* eslint-disable max-statements */
/* eslint-disable complexity */
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';
import {
  dateTimeToDate,
  isValidDateString,
  dateTimeToTime,
  isValidTimeString,
  deconstructValuesFromDateString,
} from '../utils/stringHelpers.js';
import { convertToPositiveNumberIfPossible } from '../utils/numberHelpers.js';
import { getIsDateTime, getIsDate, getIsTime } from '../utils/typeGuards.js';
const PREPEND = `ControlledDateTimePicker:
  Invalid configuration detected::
  
    `;
const NOT_A_DEFECT = '';
// '\n\n\t\tThis is not a defect, please check\n\n\t\thttps://qa.dimsum.d1.ice.com/?path=/story/components-desktop-components-d-controlledform-date-time-picker--basic\n\n\t\tfor instructions on how to use the component correctly\n\n';
const INLINE_RECEIVED_PREPEND = `

    Received:`;
const throwInvalidDateTimeError = (invalidValue: string) => {
  throw new Error(
    `${PREPEND}the "dateTime" you are providing is not respecting the format it must respect.${INLINE_RECEIVED_PREPEND}${invalidValue}(${typeof invalidValue})${NOT_A_DEFECT}`,
  );
};
const throwInvalidDaterror = (invalidValue: string) => {
  throw new Error(
    `${PREPEND}the "date" you are providing is not respecting the format it must respect.${INLINE_RECEIVED_PREPEND}${invalidValue}(${typeof invalidValue})${NOT_A_DEFECT}`,
  );
};
const throwInvalidTimerror = (invalidValue: string) => {
  throw new Error(
    `${PREPEND}the "time" you are providing is not respecting the format it must respect.${INLINE_RECEIVED_PREPEND}${invalidValue}(${typeof invalidValue})${NOT_A_DEFECT}`,
  );
};
const throwUncontrolledDateError = (date: string, onDateChange: unknown) => {
  throw new Error(
    `${PREPEND}you are trying to use a date selector while providing a non-string "date" or providing a non-function to "onDateChange"
  the full-controlled pattern must be used\n\tcheck https://reactjs.org/docs/forms.html#controlled-components for more informations

  Received:
    date: ${date}(${typeof date})
    onDateChange: (${typeof onDateChange})
  ${NOT_A_DEFECT}`,
  );
};
const throwUncontrolledTimeError = (time: string, onTimeChange: unknown) => {
  throw new Error(
    `${PREPEND}you are trying to use a time selector while providing a non-string "time" or providing a non-function to "onTimeChange"
  the full-controlled pattern must be used\n\tcheck https://reactjs.org/docs/forms.html#controlled-components for more informations

  Received:
    time: ${time}(${typeof time})
    onTimeChange: (${typeof onTimeChange})
  ${NOT_A_DEFECT}`,
  );
};

const throwUncontrolledDateTimeError = (dateTime: string, onDateTimeChange: unknown) => {
  throw new Error(
    `${PREPEND}you are trying to use a date-time selector while providing a non-string "dateTime" or providing a non-function to "onDateTimeChange"
  the full-controlled pattern must be used\n\tcheck https://reactjs.org/docs/forms.html#controlled-components for more informations

  Received:
    dateTime: ${dateTime}(${typeof dateTime})
    onDateTimeChange: (${typeof onDateTimeChange})
  ${NOT_A_DEFECT}`,
  );
};
const throwInvalidEmptyPickerStartingMonthError = (emptyPickerStartingMonth: string) => {
  throw new Error(
    `${PREPEND}the provided emptyPickerStartingMonth props is invalid. please provide a string with the format mm/__/yyyy, (mm 01~12, yyyy 0001~9999) 

  Received:
    ${emptyPickerStartingMonth}(${typeof emptyPickerStartingMonth})
  ${NOT_A_DEFECT}`,
  );
};
const throwInvalidOnCalendarOpenFocusedDayError = (onCalendarOpenFocusedDay: string) => {
  throw new Error(
    `${PREPEND}the provided onCalendarOpenFocusedDay props is invalid. please provide a string with the format mm/dd/yyyy, (mm 01~12, dd 01~31, yyyy 0001~9999) 

  Received:
    ${onCalendarOpenFocusedDay}(${typeof onCalendarOpenFocusedDay})
  ${NOT_A_DEFECT}`,
  );
};
export const useValidateProps = (props: DSControlledDateTimePickerT.Props): void => {
  // validate date-related props
  if (getIsDateTime(props) || getIsDate(props)) {
    const { emptyPickerStartingMonth, onCalendarOpenFocusedDay } = props;

    // validate generic use-cases
    if (emptyPickerStartingMonth) {
      const { month: stringMonthVal, year: stringYearVal } = deconstructValuesFromDateString(emptyPickerStartingMonth);
      const monthNum = convertToPositiveNumberIfPossible(stringMonthVal);
      const yearNum = convertToPositiveNumberIfPossible(stringYearVal);
      if (monthNum <= 0 || monthNum >= 13 || yearNum <= 0 || yearNum > 9999)
        throwInvalidEmptyPickerStartingMonthError(emptyPickerStartingMonth);
    }
    if (onCalendarOpenFocusedDay) {
      const {
        month: stringMonthVal,
        day: stringDayVal,
        year: stringYearVal,
      } = deconstructValuesFromDateString(onCalendarOpenFocusedDay);
      const monthNum = convertToPositiveNumberIfPossible(stringMonthVal);
      const dayNum = convertToPositiveNumberIfPossible(stringDayVal);
      const yearNum = convertToPositiveNumberIfPossible(stringYearVal);
      if (monthNum <= 0 || monthNum >= 13 || dayNum <= 0 || dayNum > 31 || yearNum <= 0 || yearNum > 9999)
        throwInvalidOnCalendarOpenFocusedDayError(onCalendarOpenFocusedDay);
    }
  }
  // validate full datetime cases
  if (getIsDateTime(props)) {
    const { dateTime, onDateTimeChange } = props;
    if (typeof dateTime !== 'string' || typeof onDateTimeChange !== 'function')
      throwUncontrolledDateTimeError(dateTime, onDateTimeChange);
    else if (!isValidDateString(dateTimeToDate(dateTime)) || !isValidTimeString(dateTimeToTime(dateTime)))
      throwInvalidDateTimeError(dateTimeToTime(dateTime));
  }

  // validate date-only cases
  if (getIsDate(props)) {
    const { date, onDateChange } = props;
    if (typeof date !== 'string' || typeof onDateChange !== 'function') throwUncontrolledDateError(date, onDateChange);
    else if (!isValidDateString(date)) throwInvalidDaterror(date);
  }

  // validate time-only cases

  if (getIsTime(props)) {
    const { time, onTimeChange } = props;
    if (typeof time !== 'string' || typeof onTimeChange !== 'function') throwUncontrolledTimeError(time, onTimeChange);
    else if (!isValidTimeString(time)) throwInvalidTimerror(time);
  }
};
